import { ValidationError } from './apiError';
import { isLeft } from 'fp-ts/lib/Either';
import { RawApi } from '.';
import {
  articleApiResponse,
  Article,
  Summary,
  summaryApiResponse,
  Video,
  videoApiResponse,
  Country,
  countriesApiResponse,
  RelatedContent,
  relatedContentApiResponse,
} from './validators/content';

export interface ContentAPI {
  fetchContentSummary: (type: 'articles' | 'video', id: string) => Promise<Summary>;
  fetchRelatedContent: (type: 'articles' | 'video', categoryId: string) => Promise<RelatedContent>;
  fetchArticle: (id: string) => Promise<Article>;
  fetchVideo: (id: string) => Promise<Video>;
  fetchCountries: () => Promise<Country[]>;
}

export class ContentApi extends RawApi implements ContentAPI {
  public async fetchContentSummary(
    type: 'articles' | 'video',
    id: string,
  ): Promise<Summary> {
    const response = await this.fetchScoresApi(`/content/v5/${type}/${id}/summary`);
    const jsonResponse: unknown = await response.json();
    const articleSummary = summaryApiResponse.decode(jsonResponse);

    if (isLeft(articleSummary)) {
      throw new ValidationError('API Response validation failed', 400, articleSummary);
    }

    return articleSummary.right;
  }

  public async fetchArticle(
    id: string,
  ): Promise<Article> {
    const response = await this.fetchScoresApi(`/content/v5/articles/${id}`);
    const jsonResponse: unknown = await response.json();
    const article = articleApiResponse.decode(jsonResponse);

    if (isLeft(article)) {
      throw new ValidationError('API Response validation failed', 400, article);
    }

    return article.right;
  }

  public async fetchRelatedContent(
    type: 'articles' | 'video',
    categoryId: string,
  ): Promise<RelatedContent> {
    const response = await this.fetchScoresApi(`/content/v5/${type}/category/${categoryId}`);
    const jsonResponse: unknown = await response.json();
    const relatedContentSummary = relatedContentApiResponse.decode(jsonResponse);

    if (isLeft(relatedContentSummary)) {
      throw new ValidationError('API Response validation failed', 400, relatedContentSummary);
    }

    return relatedContentSummary.right;
  }

  public async fetchVideo(
    id: string,
  ): Promise<Video> {
    const response = await this.fetchScoresApi(`/content/v5/video/${id}`);
    const jsonResponse: unknown = await response.json();
    const video = videoApiResponse.decode(jsonResponse);

    if (isLeft(video)) {
      throw new ValidationError('API Response validation failed', 400, video);
    }

    return video.right;
  }

  public async fetchCountries(): Promise<Country[]> {
    const response = await this.fetchScoresApi('/content/v5/countries');
    const jsonResponse: unknown = await response.json();
    const countries = countriesApiResponse.decode(jsonResponse);

    if (isLeft(countries)) {
      throw new ValidationError('API Response validation failed', 400, countries);
    }

    return countries.right;
  }
}
